import { get, ref, set, update } from 'firebase/database';
import { httpsCallable } from 'firebase/functions';
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from 'firebase/storage';
import { db, functions, storage } from '../config/firebase';

/** Client wait limit (ms). Default Firebase callable timeout is ~70s — too low for trilingual lesson AI. */
const DEFAULT_CALLABLE_MS = 70000;
const LONG_RUNNING_CALLABLE_MS = 540000; // 9 minutes (stay under typical 540s function limit)

const callPlannerFunction = async (functionName, payload, { timeoutMs = DEFAULT_CALLABLE_MS } = {}) => {
  const callable = httpsCallable(functions, functionName, { timeout: timeoutMs });
  const response = await callable(payload);
  return response.data;
};

export const createLessonPlannerDraft = (courseId) =>
  callPlannerFunction('createLessonPlannerDraft', { courseId });

export const generateCourseStructure = (draftId) =>
  callPlannerFunction('generateCourseStructure', { draftId }, { timeoutMs: LONG_RUNNING_CALLABLE_MS });

export const generateLessons = (draftId) =>
  callPlannerFunction('generateLessons', { draftId }, { timeoutMs: LONG_RUNNING_CALLABLE_MS });

export const generateLessonForSubtopic = (draftId, topicId, subtopicId) =>
  callPlannerFunction(
    'generateLessonForSubtopic',
    {
      draftId,
      topicId,
      subtopicId,
    },
    { timeoutMs: LONG_RUNNING_CALLABLE_MS },
  );

export const generateLessonImage = (draftId, lessonId, prompt = '') =>
  callPlannerFunction(
    'generateLessonImage',
    { draftId, lessonId, prompt },
    { timeoutMs: LONG_RUNNING_CALLABLE_MS },
  );

export const regenerateDraftItem = (draftId, itemType, itemId, mode = '') =>
  callPlannerFunction(
    'regenerateDraftItem',
    {
      draftId,
      itemType,
      itemId,
      mode,
    },
    { timeoutMs: LONG_RUNNING_CALLABLE_MS },
  );

export const generateAssignmentsAndCover = (draftId) =>
  callPlannerFunction(
    'generateAssignmentsAndCover',
    { draftId },
    { timeoutMs: LONG_RUNNING_CALLABLE_MS },
  );

export const publishLessonPlannerDraft = (draftId) =>
  callPlannerFunction('publishLessonPlannerDraft', { draftId });

export const fetchLessonPlannerDraft = async (draftId) => {
  const snapshot = await get(ref(db, `lessonPlannerDrafts/${draftId}`));
  return snapshot.val();
};

export const saveLessonPlannerDraft = async (draftId, draft) => {
  await set(ref(db, `lessonPlannerDrafts/${draftId}`), {
    ...draft,
    updatedAt: new Date().toISOString(),
  });
};

export const updateLessonPlannerDraft = async (draftId, patch) => {
  await update(ref(db, `lessonPlannerDrafts/${draftId}`), {
    ...patch,
    updatedAt: new Date().toISOString(),
  });
};

export const uploadLessonPlannerAsset = async ({
  draftId,
  file,
  assetType,
  itemId = '',
}) => {
  const filePath = itemId
    ? `lesson-planner/${draftId}/${assetType}/${itemId}/${Date.now()}_${file.name}`
    : `lesson-planner/${draftId}/${assetType}/${Date.now()}_${file.name}`;

  const fileRef = storageRef(storage, filePath);
  const snapshot = await uploadBytes(fileRef, file, {
    contentType: file.type,
  });
  return getDownloadURL(snapshot.ref);
};
